import { ExternalPricingPackage } from '@va/aaas/shared/types';
import { useFetchData } from '@va/util/hooks';
import { useCallback } from 'react';
import { AaasPaymentsApi, GetPricingPlansApiResponse, PricingPlan, PricingPlanPrice } from './billing';

const mapPricingPackageToPlanPrice = (pak: ExternalPricingPackage): PricingPlanPrice => {
  return {
    aaasPackageId: pak.aaasPackageId,
    billingInterval: pak.billingInterval,
    currency: pak.currency,
    default: false,
    recommended: pak.recommended,
    stripePriceId: pak.stripePriceId,
    taxBehaviour: pak.taxBehaviour,
    unitAmount: pak.unitAmount,
  };
};

const mapPricingPlansResponse = ({ payload, meta }: GetPricingPlansApiResponse) => {
  const pricingPlansByStripeProductId: Record<string, PricingPlan> = {};

  for (const pak of payload) {
    if (pricingPlansByStripeProductId[pak.stripeProductId]) {
      pricingPlansByStripeProductId[pak.stripeProductId].prices.push(mapPricingPackageToPlanPrice(pak));
    } else {
      pricingPlansByStripeProductId[pak.stripeProductId] = {
        description: pak.description,
        name: pak.name,
        recommended: pak.recommended,
        stripeProductId: pak.stripeProductId,
        touchpoints: pak.touchpoints,
        prices: [mapPricingPackageToPlanPrice(pak)],
      };
    }
  }

  const plans = Object.values(pricingPlansByStripeProductId).sort((a, b) => a.touchpoints - b.touchpoints);

  return { plans, defaultCurrency: meta?.defaultCurrency };
};

export const useAaasPricingPlans = ({
  websiteId,
  subscriptionId,
  enabled = true,
}: {
  websiteId: string;
  subscriptionId: string;
  enabled?: boolean;
}) => {
  const fetcherFn = useCallback(async () => await AaasPaymentsApi.getPricingPlans(subscriptionId), [subscriptionId]);

  return useFetchData(enabled ? ['pricing-plans', websiteId] : null, undefined, mapPricingPlansResponse, fetcherFn);
};

// An existing stripe subscription can't change its billing currency, so only plans priced in that currency are eligible
export const filterPricingPlansByCurrency = (plans: PricingPlan[], currency?: string) => {
  if (!currency) return plans;

  const result: PricingPlan[] = [];

  plans.forEach((plan) => {
    const okPrices = plan.prices.filter((price) => price.currency === currency);

    if (okPrices.length !== 0) {
      result.push({ ...plan, prices: okPrices });
    }
  });

  return result;
};

/**
 * @param plans Must be sorted ascending by touchpoints
 */
export const findMinimumRequiredPlan = (
  plans: PricingPlan[],
  requiredCredits: number,
  activePackageTouchpoints = 0,
) => {
  return plans.find((plan) => plan.touchpoints >= requiredCredits && plan.touchpoints > activePackageTouchpoints);
};
